// src/screens/VerifyEmailScreen.js
// Opened from the welcome email's "Confirm Email & Get Started" button.
// Confirms the address; for customers the API also signs them in (session
// cookie), and we take them to Vehicles. The link works once.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authAPI } from '../services/api';

const primaryButton =
  'block w-full py-4 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover text-white text-base font-bold text-center shadow-lg shadow-customer-teal/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed';

const VerifyEmailScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, completeSignIn } = useContext(AuthContext);

  const [status, setStatus] = useState('verifying'); // verifying | verified | failed
  const [signedIn, setSignedIn] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    // The link works once, so verify only once (React may run effects twice
    // in development).
    if (started.current) return;
    started.current = true;

    const token = searchParams.get('token');
    // Keep the token out of the address bar and browser history.
    navigate('/verify-email', { replace: true });

    if (!token) {
      setError('This link is incomplete. Request a new one below.');
      setStatus('failed');
      return;
    }

    authAPI.verifyEmail(token)
      .then(({ data }) => {
        setStatus('verified');
        if (data.user) {
          completeSignIn(data.user);
          setSignedIn(true);
          setTimeout(() => navigate('/vehicles', { replace: true }), 1500);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'We could not verify your email.');
        setStatus('failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async (e) => {
    e.preventDefault();
    setResending(true);
    try {
      await authAPI.resendVerification(user?.email || email);
    } catch {
      // The answer is the same either way (it never reveals which emails exist).
    }
    setResent(true);
    setResending(false);
  };

  return (
    <div className="flex flex-col min-h-screen lg:min-h-0 lg:py-12 lg:px-4 bg-gradient-to-br from-customer-teal-light/30 via-white to-customer-accent-light/30 dark:from-app-bg dark:via-app-bg dark:to-app-bg customer-friendly">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center py-8 lg:py-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-app-text-light dark:text-app-text mb-2">
            {status === 'verifying' && 'Confirming your email...'}
            {status === 'verified' && 'Email confirmed'}
            {status === 'failed' && 'Link not valid'}
          </h1>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-5 text-sm w-full max-w-md mx-auto bg-white dark:bg-app-bg-secondary rounded-3xl shadow-xl shadow-customer-teal/10 border border-app-border-light/50 dark:border-app-border">
        {status === 'verifying' && (
          <p className="text-center text-app-text-secondary-light dark:text-app-text-secondary">One moment...</p>
        )}

        {status === 'verified' && (
          <div className="text-center space-y-4">
            <div className="text-5xl">✅</div>
            <p className="text-app-text-light dark:text-app-text">
              {signedIn
                ? "You're signed in and ready to book test drives. Taking you to the vehicles..."
                : 'Your email address is confirmed.'}
            </p>
            {signedIn ? (
              <Link to="/vehicles" replace className={primaryButton}>Browse Vehicles</Link>
            ) : (
              <Link to={user ? '/' : '/login'} replace className={primaryButton}>{user ? 'Continue' : 'Sign In'}</Link>
            )}
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-4">
            <p className="text-app-text-light dark:text-app-text">
              {error} Links expire after 24 hours and can be used once. If you already confirmed your email, just sign in.
            </p>

            {resent ? (
              <p className="text-app-text-light dark:text-app-text">
                If that address is on file and not yet confirmed, a new link is on its way. Check your inbox.
              </p>
            ) : (
              <form onSubmit={resend} className="space-y-3">
                {!user && (
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-teal/50 focus:border-customer-teal transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                )}
                <button type="submit" disabled={resending} className={primaryButton}>
                  {resending ? 'Sending...' : 'Send a new link'}
                </button>
              </form>
            )}

            <div className="text-sm text-center pt-4 border-t border-app-border-light/50 dark:border-app-border">
              <Link to="/login" className="text-customer-accent font-semibold hover:text-customer-accent-hover transition-colors">
                Sign in instead
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailScreen;
